import { CarsWidgetSliderProps } from 'features/cars-widget/types'
import React, { FC, useEffect, useMemo } from 'react'
import styled, { useTheme } from 'styled-components'
import { Button } from 'ui/button'
import { Card } from 'ui/card/components'
import { Slide, Slider } from 'ui/slider'

import { URL_STRINGIFY_OPTIONS } from '../../../../application/configuration'
import { appendUrl } from '../../../../lib/append-url'
import { getProjectName } from '../../../../lib/get-project-name'
import { replacePlaceholders } from '../../../../lib/placeholders'
import { createPluralForm } from '../../../../lib/pluralize'
import { capitalize } from '../../../../lib/string-case-transforms'
import { usePriceFormatter } from '../../../../lib/use-price-formatter'
import { Row } from '../../../../ui/grid'
import { SkeletonButton } from '../../../../ui/skeleton-button'
import { SkeletonCard } from '../../../../ui/skeleton-card'
import { useCarsForWidget } from '../../useCarsForWidget'

export const CarsWidgetSlider: FC<CarsWidgetSliderProps> = (props) => {
  const appTheme = useTheme()
  const containerGutters = useTheme().grid.containerGutters
  const paddingForSliderOverflow =
    containerGutters !== undefined ? `calc(${containerGutters} / 2 - ${appTheme?.spacing[5]})` : undefined

  const { cars, fetching, minPrice, totalCount } = useCarsForWidget(props)

  const ctaButtonLink = useMemo(() => {
    if (props?.button) {
      return appendUrl({
        url: props?.button?.carsListUrl,
        parseOptions: URL_STRINGIFY_OPTIONS,
        stringifyOptions: URL_STRINGIFY_OPTIONS,
        data: props?.filterValues
      })
    }
    return
  }, [])

  const formatter = usePriceFormatter()

  const ctaButtonText = useMemo(() => {
    if (props.button) {
      return capitalize(
        replacePlaceholders(props.button.buttonTextTemplate, {
          '${cars.pluralized.found}': createPluralForm(...props.button.foundForms)(totalCount),
          '${cars.count}': `${totalCount}`,
          '${cars.pluralized.cars}': createPluralForm(...props.button.carsForms)(totalCount),
          '${cars.prices.min}': formatter(minPrice || 0)
        })
      )
    }
    return
  }, [totalCount, minPrice])

  const Slides = useMemo(() => {
    if (fetching)
      return Array.from({ length: props?.carsInSlider }, (_, i) => (
        <Slide key={i}>
          <CardWrapper>
            {fetching && <SkeletonCard ratio={props?.carViewSettings?.images?.ratio || { x: 16, y: 9 }} />}
          </CardWrapper>
        </Slide>
      ))

    if (cars && cars?.length > 0) {
      return cars?.map((car) => (
        <Slide key={car?.id}>
          <CardWrapper>
            <Card
              carData={car}
              carViewSettings={props?.carViewSettings}
              smartContractEnabled={false}
              linkTarget={props?.openCardsLinksInNewTab ? '_blank' : '_self'}
              tooltipSettings={{ place: 'bottom' }}
            />
          </CardWrapper>
        </Slide>
      ))
    }

    return null
  }, [fetching, cars])

  useEffect(() => {
    /*Очистка памяти*/
    return () => {}
  }, [])

  return (
    <>
      <SliderWrapper>
        {cars && cars?.length > 0 && (
          <Slider
            slidesPerPage={props?.sliderConfig?.breakpoints}
            slidesPerGroup={1}
            paddingForOverflow={paddingForSliderOverflow}
            arrowPosition={appTheme?.spacing[24]}
            arePositionIconsTop
          >
            {Slides}
          </Slider>
        )}
      </SliderWrapper>
      {props?.button && cars && cars?.length > 0 && (
        <div style={{ marginTop: '4rem' }}>
          <Row justifyContent={'center'}>
            {fetching && <SkeletonButton block={true} />}
            {!fetching && (
              <Button block={props?.button?.block} secondary={props?.button?.type === 'secondary'} link={ctaButtonLink}>
                {ctaButtonText}
              </Button>
            )}
          </Row>
        </div>
      )}
    </>
  )
}

const SliderWrapper = styled.div`
  margin: ${() => (getProjectName() === 'mercedes-benz' ? 0 : `5.6rem`)}
    calc(${({ theme }) => theme.grid.containerGutters} / 2 * -1) 0;
`

const CardWrapper = styled.div`
  height: 100%;
  padding-right: ${({ theme }) => theme.spacing[3]};
  padding-left: ${({ theme }) => theme.spacing[3]};
`
